import { Receipt } from '@mui/icons-material';
import { Typography } from '@mui/material';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getIntegrationStatusByInternalName } from '../../../../../../lib/api';
import { getCompanyLogo } from '../../../../../../lib/api/company';
import { expressGetCompanyLogo } from '../../../../../../lib/api/express';
import {
  getActiveMoneyReports,
  getDocumentReport,
} from '../../../../../../lib/api/reports';
import { getAECsInPosession } from '../../../../../../lib/api/reSell/factoring';
import { getSelectedCertificate } from '../../../../../../lib/api/SIICertificates';
import CenteredCircularProgress from '../../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from '../../../../../../lib/designSystem/HeaderWithActions';
import TableWithPagination from '../../../../../../lib/designSystem/TableWithPagination';
import { LightTooltip } from '../../../../../../lib/designSystem/WhiteTooltip';
import { showAlert } from '../../../../../../lib/functions/utils';
import { AuthState } from '../../../../../../services/redux/authSlice';
import { generateDocumentReportPDF } from '../../../Financing/common/exportDocumentReport';
import DebtProjector from './components/DebtProjector';

const ActiveMoney: React.FC = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDocuments, setSelectedDocuments] = useState<any>([]);
  const [open, setOpen] = useState(false);
  const [dataType, setDataType] = useState('active');
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [stakeholderIdentifier, setStakeholderIdentifier] = useState('');
  const [stakeholderName, setStakeholderName] = useState('');
  const [documentType, setDocumentType] = useState('factoring');
  const [requestId, setRequestId] = useState('');
  const [folio, setFolio] = useState('');
  const dispatch = useDispatch();
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const [selectedCertificate, setSelectedCertificate] = useState<any>(null);
  const [hasCessionIntegration, setHasCessionIntegration] = useState(false);
  const [startOperationDate, setStartOperationDate] = useState<any>(null);
  const [endOperationDate, setEndOperationDate] = useState<any>(null);
  const generateQuery = ({
    page,
    pageSize,
    exportComplete,
  }: {
    page: number;
    pageSize: number;
    exportComplete: boolean;
  }) => {
    let query = '';
    if (documentType) query += `documentType=${documentType}&`;
    if (folio) query += `folio=${folio}&`;
    if (dataType) query += `dataType=${dataType}&`;
    if (businessIdentifier)
      query += `businessIdentifier=${businessIdentifier}&`;
    if (businessName) query += `businessName=${businessName}&`;
    if (stakeholderIdentifier)
      query += `stakeholderIdentifier=${stakeholderIdentifier}&`;
    if (stakeholderName) query += `stakeholderName=${stakeholderName}&`;
    if (requestId) query += `requestId=${requestId}&`;
    if (page) query += `page=${page}&pageSize=${pageSize}&`;
    if (exportComplete) query += `exportComplete=true&`;
    if (startOperationDate)
      query += `startOperationDate=${startOperationDate}&`;
    if (endOperationDate) query += `endOperationDate=${endOperationDate}`;
    return query;
  };

  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  const handleDownloadReport = async (id: string) => {
    setLoading(true);
    try {
      // const documentsApplied = await getDocumentsApplied();
      const response = await getDocumentReport(dispatch, id);
      const imageBase64 = user?.express
        ? await expressGetCompanyLogo(dispatch, user.clientId as string)
        : await getCompanyLogo(dispatch);
      await generateDocumentReportPDF(response, user?.name || '', imageBase64);
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const handleDownloadAEC = async (id: string) => {
    setLoading(true);
    try {
      // const documentsApplied = await getDocumentsApplied();
      const response = await getAECsInPosession(dispatch, [id]);
      if (response[0].status) {
        const xmlResponse = await fetch(response.ArchivoBase64);
        const xmlText = await xmlResponse.text();

        // Crear un blob y descargarlo
        const blob = new Blob([xmlText], { type: 'application/xml' });
        const downloadUrl = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `aec-${id}.xml`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
        showAlert(dispatch, 'success', 'AEC descargado correctamente');
      } else {
        showAlert(
          dispatch,
          'error',
          'No pudimos descargar el AEC, es probable que ya no seas el tenedor del AEC.',
        );
      }
    } catch (error) {
      showAlert(dispatch, 'error', 'Error inesperado');
      console.log(error);
    }
    setLoading(false);
  };

  const handleDownloadReportObject = {
    key: 'downloadReport',
    label: '',
    handleAction: handleDownloadReport,
    icon: (
      <LightTooltip
        title={<Typography padding={2}>Descargar informe</Typography>}
      >
        <Receipt />
      </LightTooltip>
    ),
  };

  const handleDownloadAECObject = {
    key: 'downloadAEC',
    label: '',
    handleAction: handleDownloadAEC,
    icon: <Typography fontWeight={800}>AEC</Typography>,
    keyDisabled: 'canShowAec',
  };

  const setUpCertificates = async () => {
    try {
      const response = await getSelectedCertificate(dispatch);
      setSelectedCertificate(response);
    } catch (error) {
      console.log(error);
    }
  };

  const checkCessionIntegration = async () => {
    try {
      const response = await getIntegrationStatusByInternalName(
        dispatch,
        'recesion',
      );
      setHasCessionIntegration(response.status);
    } catch (error) {
      console.log(error);
    }
  };

  const setUp = async () => {
    setLoading(true);
    await setUpCertificates();
    await checkCessionIntegration();
    await setUpData({ page: 0, pageSize: PAGE_SIZE });
    setLoading(false);
  };

  const canShowDownloadAEC = hasCessionIntegration && selectedCertificate;

  const setUpData = async ({
    page = 0,
    pageSize = PAGE_SIZE,
    exportComplete = false,
  }: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }): Promise<{
    data: any[];
    pagination: {
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
    };
  }> => {
    const query = generateQuery({ page, pageSize, exportComplete });
    try {
      const response = await getActiveMoneyReports(dispatch, query);
      if (!exportComplete) {
        setData(response.data);
        setPagination(response.pagination);
      }
      return response;
    } catch (error) {
      console.log(error);
      return {
        data: [],
        pagination: { page, pageSize, total: 0, totalPages: 0 },
      };
    }
  };
  useEffect(() => {
    setUp();
  }, []);

  const filters: Filter[] = [
    {
      label: 'Fecha de operación',
      type: 'date',
      value: startOperationDate,
      setter: setStartOperationDate,
      dateKey: 'startDate',
    },
    {
      label: 'Fecha de operación',
      type: 'date',
      value: endOperationDate,
      setter: setEndOperationDate,
      dateKey: 'endDate',
    },
    {
      label: 'Tipo de cartera',
      type: 'select',
      value: dataType,
      setter: setDataType,
      options: [
        { label: 'Activa', value: 'active' },
        { label: 'Completada', value: 'completed' },
        { label: 'Todas', value: 'all' },
      ],
    },
    {
      label: 'Tipo de documento',
      type: 'select',
      value: documentType,
      setter: setDocumentType,
      options: [
        { label: 'Factoring', value: 'factoring' },
        { label: 'Crédito', value: 'credit' },
        { label: 'Confirming', value: 'confirming' },
      ],
    },
    {
      label: 'Nombre cliente',
      type: 'autocomplete',
      value: businessName,
      key: 'businessName',
      setter: setBusinessName,
    },
    {
      label: 'Rut emisor',
      type: 'text',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      label: 'Nombre deudor',
      type: 'autocomplete',
      value: stakeholderName,
      key: 'stakeholderName',
      setter: setStakeholderName,
    },
    {
      label: 'Rut contraparte',
      type: 'text',
      value: stakeholderIdentifier,
      setter: setStakeholderIdentifier,
    },
    {
      label: 'Id de solicitud',
      type: 'text',
      value: requestId,
      setter: setRequestId,
    },
    {
      label: 'Folio',
      type: 'text',
      value: folio,
      setter: setFolio,
    },
  ];

  const triggerSearch = async () => {
    setLoading(true);
    await setUpData({ page: 0, pageSize: PAGE_SIZE });
    setLoading(false);
  };
  const buttons: CustomButtonProps[] = [
    {
      action: () => setOpen(true),
      name: 'Proyectar mora',
      color: 'secondary',
      disabled: selectedDocuments.length === 0 ? true : false,
    },
    {
      action: () => {},
      color: 'primary',
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={triggerSearch}
          dispatch={dispatch}
          disableSearch={loading}
        />
      ),
    },
  ];

  if (loading) return <CenteredCircularProgress type="layout" />;
  return (
    <div>
      <HeaderWithActions
        title="Acá encontrarás todos los documentos de tu cartera de clientes."
        variant="body1"
        buttons={buttons}
      />
      <TableWithPagination
        dataHeaders={[
          handleDownloadReportObject,
          ...(canShowDownloadAEC ? [handleDownloadAECObject] : []),
          { key: 'documentType', label: 'Tipo documento' },
          { key: 'documentNumber', label: 'Folio' },
          { key: 'operationId', label: 'Id Solicitud', type: 'numeric-id' },
          { key: 'amount', label: 'Monto documento', type: 'money' },
          { key: 'dueDate', label: 'Fecha vencimiento', type: 'date' },
          { key: 'businessIdentifier', label: 'Rut cliente', type: 'report' },
          { key: 'businessName', label: 'Nombre cliente' },
          { key: 'stakeholderIdentifier', label: 'Rut deudor' },
          { key: 'stakeholderName', label: 'Nombre deudor' },
          { key: 'operationDate', label: 'Fecha operación', type: 'date' },
          { key: 'issuedDate', label: 'Emisión', type: 'date' },
          { key: 'cancelDate', label: 'Rechazo', type: 'date' },
          { key: 'confirmDate', label: 'Confirmación', type: 'date' },
          {
            key: 'creditNoteAmount',
            label: 'Monto nota crédito',
            type: 'money',
          },
          { key: 'lastCollectionManagementStatus', label: 'E. Cobranza' },
          { key: 'lastCollectionManagementOption', label: 'S. Cobranza' },
          { key: 'conciliationAmount', label: 'Abono', type: 'money' },
          {
            key: 'lastConciliationDate',
            label: 'Último abono',
            type: 'date',
          },
          { key: 'leftAmount', label: 'Saldo documento', type: 'money' },
          {
            key: 'anticipatedAmount',
            label: 'Monto anticipado',
            type: 'money',
          },
          {
            key: 'rate',
            label: 'Tasa negociada',
            type: 'percentage-soft',
          },
          {
            key: 'priceDifference',
            label: 'Diferencia de precio',
            type: 'money',
          },
          { key: 'dailyAccrual', label: 'Devengo diario', type: 'money' },
          { key: 'arrearDays', label: 'Días mora' },
          { key: 'arrearAmount', label: 'Interés mora', type: 'money' },
          { key: 'documentState', label: 'Estado documento' },
          { key: 'economicSector', label: 'Sector económico' },
          { key: 'executive', label: 'Ejecutivo' },
          { key: 'fundType', label: 'Fondo' },
          { key: 'resellDate', label: 'Fecha fondo', type: 'date' },
          { key: 'resellRate', label: 'Tasa venta', type: 'percentage-soft' },
          { key: 'fundCostTotal', label: 'Costo total fondo', type: 'money' },
          { key: 'stillInResell', label: 'Estado Fondo', type: 'boolean' },
        ]}
        data={data}
        selected={selectedDocuments}
        setSelected={setSelectedDocuments}
        rowsPerPageDefault={100}
        loading={loading}
        searchData={setUpData}
        exportToFile="xlsx"
        exportComplete
        setLoading={setLoading}
        pagination={pagination}
        setPagination={setPagination}
        setData={setData}
        lastElement
      />
      <DebtProjector
        open={open}
        documents={data.filter((document: any) =>
          selectedDocuments.includes(document.id),
        )}
        setOpen={setOpen}
      />
    </div>
  );
};

export default ActiveMoney;
